const IMAGE_HOSTS = new Set(["images.eatzy.com", "images.eatsy.net"]);
// GIFs may be animated; resizing through image negotiation can flatten them.
const IMAGE_PATH = /\.(?:jpe?g|png|webp|avif)$/i;
// The public /media/users/{id}/{sha1} photo shape was verified against the
// production image Worker (JPEG original, width=768 negotiated AVIF). This is
// only eligible on a caller that already knows it is displaying a photo.
const VERIFIED_EXTENSIONLESS_PHOTO_PATH = /^\/media\/users\/\d+\/[a-f0-9]{40}$/i;
const SIGNED_PARAMETER = /(?:sign|token|auth|credential|policy|expires|secret|key|hmac|jwt|hash)|^(?:s|sig|exp)$/i;

/**
 * Only the two confirmed image Workers accept width/height transforms.
 * Unknown/extensionless assets are deliberately left alone: they may be files,
 * video, or opaque signed resources. Never migrate a host or stored URL here.
 * allowVerifiedPhotoPath is only for known photo surfaces, not arbitrary media.
 */
export function sizedCdnImage(
  src: string,
  width: number,
  height?: number,
  allowVerifiedPhotoPath = false,
): string {
  if (!Number.isFinite(width) || width <= 0 ||
      (height !== undefined && (!Number.isFinite(height) || height <= 0))) return src;
  try {
    const url = new URL(src);
    if (url.protocol !== "https:" || !IMAGE_HOSTS.has(url.hostname) ||
        url.port || url.username || url.password ||
        !(IMAGE_PATH.test(url.pathname) ||
          (allowVerifiedPhotoPath && VERIFIED_EXTENSIONLESS_PHOTO_PATH.test(url.pathname))) ||
        Array.from(url.searchParams.keys()).some((key) => SIGNED_PARAMETER.test(key))) return src;
    url.searchParams.set("width", String(Math.min(1280, Math.ceil(width))));
    if (height !== undefined) {
      url.searchParams.set("height", String(Math.min(1280, Math.ceil(height))));
    } else {
      // Do not retain an API thumbnail's height when requesting a wider photo.
      // Width-only transforms preserve the original aspect ratio.
      url.searchParams.delete("height");
    }
    return url.toString();
  } catch {
    return src;
  }
}

/** Cap density at 2x and bucket widths to avoid many near-identical variants. */
export function imageRequestWidth(cssWidth: number, dpr = 1): number {
  if (!Number.isFinite(cssWidth) || cssWidth <= 0) return 0;
  const density = Number.isFinite(dpr) ? Math.max(1, Math.min(2, dpr)) : 1;
  return Math.min(1280, Math.ceil(cssWidth * density / 32) * 32);
}